import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"
import "tom-select/dist/css/tom-select.css"

export default class extends Controller {
  static targets = ["name", "uai", "establishmentId", "city", "zipCode", "type"]
  static values = {
    searchPath: { type: String, default: "/schools/searches" },
    minQueryLength: { type: Number, default: 2 },
    forceReselection: { type: Boolean, default: false }
  }

  connect() {
    if (!this.hasNameTarget) return

    if (this.forceReselectionValue) this.clearAutoFilledInputs()

    const initialName = this.nameTarget.value
    this.tomSelect = new TomSelect(this.nameTarget, this.tomSelectConfig())
    // Scopes the dropdown styling (create line rendered last, see _school-autocomplete.scss)
    this.tomSelect.wrapper.classList.add("school-autocomplete")

    if (initialName) {
      this.preloadInitialOption(initialName)
    }

    this.tomSelect.on("item_add", (value) => this.populateLinkedFields(value))
  }

  clearAutoFilledInputs() {
    this.nameTarget.value = ""
    if (this.hasUaiTarget) this.uaiTarget.value = ""
    if (this.hasEstablishmentIdTarget) this.establishmentIdTarget.value = ""
  }

  disconnect() {
    if (this.tomSelect) this.tomSelect.destroy()
  }

  tomSelectConfig() {
    return {
      valueField: "name",
      labelField: "name",
      searchField: ["name", "city", "zip_code"],
      create: true,
      createOnBlur: true,
      maxItems: 1,
      persist: false,
      addPrecedence: false,
      load: this.loadSchools.bind(this),
      render: {
        option: (data, escape) => {
          const subtitle = [data.zip_code, data.city].filter(Boolean).join(" ")
          return `<div>
            <div>${escape(data.name)}</div>
            ${subtitle ? `<small class="text-grey">${escape(subtitle)}</small>` : ""}
          </div>`
        },
        item: (data, escape) => `<div>${escape(data.name)}</div>`,
        no_results: (data, escape) =>
          `<div class="no-results">Aucune école trouvée pour "${escape(data.input)}".</div>`,
        option_create: (data, escape) =>
          `<div class="create">
            <span class="school-autocomplete__create-icon" aria-hidden="true">+</span>
            <span>
              Créer l'école « <strong>${escape(data.input)}</strong> »
              <small class="school-autocomplete__create-hint">Elle n'est pas dans l'annuaire ? Ajoutez-la telle quelle.</small>
            </span>
          </div>`,
        loading: () => '<div class="loading">Recherche...</div>'
      }
    }
  }

  loadSchools(query, callback) {
    if (query.length < this.minQueryLengthValue) {
      callback()
      return
    }

    fetch(`${this.searchPathValue}?q=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" }
    })
      .then(response => response.json())
      .then(json => callback(json))
      .catch(() => callback())
  }

  preloadInitialOption(initialName) {
    const option = { name: initialName }
    if (this.hasUaiTarget && this.uaiTarget.value) option.uai = this.uaiTarget.value
    if (this.hasEstablishmentIdTarget && this.establishmentIdTarget.value) option.id = this.establishmentIdTarget.value
    if (this.hasCityTarget && this.cityTarget.value) option.city = this.cityTarget.value
    if (this.hasZipCodeTarget && this.zipCodeTarget.value) option.zip_code = this.zipCodeTarget.value

    this.tomSelect.addOption(option)
    this.tomSelect.setValue(initialName, true)
  }

  populateLinkedFields(value) {
    const option = this.tomSelect.options[value]
    if (!option) return

    if (this.hasUaiTarget) this.uaiTarget.value = option.uai || ""
    // Only locally-known schools carry an id; a directory pick or free text clears it.
    if (this.hasEstablishmentIdTarget) this.establishmentIdTarget.value = option.id || ""
    if (this.hasCityTarget && option.city) this.cityTarget.value = option.city
    if (this.hasZipCodeTarget && option.zip_code) this.zipCodeTarget.value = option.zip_code
    if (this.hasTypeTarget && option.contract_type) this.typeTarget.value = option.contract_type
  }
}
